export default function Hero() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#07091a]">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/a47bb0dcba52234d02e14fbf9c5e37d3.jpg"
          alt="background"
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07091a]/70 via-[#07091a]/40 to-[#07091a]"></div>
      </div>

      {/* Radial glow — violet/indigo */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-violet-600/8 blur-3xl pointer-events-none z-0"></div>
      <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full bg-indigo-500/6 blur-3xl pointer-events-none z-0"></div>

      {/* Signal Spine Visual */}
      <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 flex flex-col items-center z-0 pointer-events-none">
        <div className="w-px h-full bg-gradient-to-b from-transparent via-violet-400/25 to-transparent"></div>
        {[20, 35, 50, 65, 80].map((pct, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-violet-400/50 animate-pulse"
            style={{ top: `${pct}%`, animationDelay: `${i * 0.4}s` }}
          ></div>
        ))}
        {[30, 50, 70].map((pct, i) => (
          <div
            key={`h-${i}`}
            className="absolute h-px bg-gradient-to-r from-transparent via-violet-400/15 to-transparent"
            style={{ top: `${pct}%`, width: '600px', left: '50%', transform: 'translateX(-50%)' }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 flex flex-col lg:flex-row items-center gap-12 lg:gap-0">
        {/* Left: Headline */}
        <div className="flex-1 text-left">
          {/* Logo mark */}
          <div className="mb-8">
            <img
              src="https://public.readdy.ai/ai/img_res/32bb7955-927e-461e-9db0-09455eaedac3.png"
              alt="SI.AI Silo Intelligence"
              className="h-24 w-auto object-contain"
            />
          </div>
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-8">
            <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse flex-shrink-0"></span>
            <span className="text-violet-300 text-xs font-medium tracking-widest uppercase whitespace-nowrap">AI-Powered Business Infrastructure</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Eliminate Silos.<br />
            <span className="bg-gradient-to-r from-violet-400 via-indigo-300 to-blue-300 bg-clip-text text-transparent">Build Intelligent</span><br />
            Systems.
          </h1>
          <p className="text-white/55 text-base md:text-lg leading-relaxed max-w-lg mb-10">
            Silo Intelligence connects your fragmented tools, leads, and workflows into one unified AI-powered system that drives measurable growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollTo('cta')}
              className="bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-semibold px-8 py-3.5 rounded-full hover:opacity-90 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              Book a Strategy Call
            </button>
            <button
              onClick={() => scrollTo('solution')}
              className="border border-white/20 text-white/80 hover:text-white hover:border-white/40 font-semibold px-8 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm flex items-center gap-2 justify-center"
            >
              <span>See How It Works</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Right: Stats */}
        <div className="flex-1 flex flex-col gap-4 lg:pl-16 w-full">
          {[
            { label: 'Built for service-driven businesses', icon: 'ri-building-2-line', val: '500+', sub: 'Businesses Served' },
            { label: 'Designed for operational clarity', icon: 'ri-dashboard-line', val: '98%', sub: 'Client Satisfaction' },
            { label: 'Focused on revenue impact', icon: 'ri-line-chart-line', val: '3.2x', sub: 'Average ROI' },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-5 bg-white/4 border border-white/8 rounded-2xl px-6 py-5 hover:border-violet-500/30 transition-colors"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-violet-500/10 border border-violet-500/20 flex-shrink-0">
                <i className={`${item.icon} text-violet-400 text-xl`}></i>
              </div>
              <div className="flex-1">
                <p className="text-white/50 text-xs mb-0.5">{item.label}</p>
                <p className="text-white font-bold text-lg leading-none">{item.val} <span className="text-white/40 text-xs font-normal">{item.sub}</span></p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/30 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/30 text-lg"></i>
      </div>
    </section>
  );
}
